import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiX, FiPlay, FiDollarSign,
  FiInfo, FiClock, FiCalendar
} from 'react-icons/fi';
import { collection, getDocs, query, orderBy, where, doc, updateDoc } from 'firebase/firestore';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton,
  LoadingMessage, TranEmptyState, TableContainer, Table, Th, Tr, Td,
  StatusBadge, ActionGroup, IconBtn,
  FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGroup, Label, Input, Select
} from '../../components/SharedStyles';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus, getTransaction
} from '../../utils/transactionHelper';
import {
  buildPayrollLine, recomputeLine, loadRecoveriesForStaff,
  applyRecoveries, reverseRecoveries, computeOtAmount, computeOtFixed
} from '../../utils/payrollHelper';
import { deductionsAboveBalance } from '../../utils/loanRecoveryHelper';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import useEscapeClose from '../../hooks/useEscapeClose';
import usePagePermissions from '../../hooks/usePagePermissions';
import PayMethodPicker from '../../components/PayMethodPicker';
import useCurrency from '../../hooks/useCurrency';
import useOpenFromReport from '../../hooks/useOpenFromReport';
import { formatAmount } from '../../utils/documentNumbering';
// Attendance add-on (module-gated): pre-fills Present/Absent/Paid Leave from machine logs.
import { loadAttendanceSettings, fetchMonthLogs, fetchMonthAdjustments, buildMonthlySummary, STATUS_META, WEEKDAY_NAMES, weekdayOf, fmt12, fmtHM, minutesOf } from '../../utils/attendanceHelper';

const TRAN_TYPE_CODE = 'PAY';
const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.info.main,
  Paid: colors.success.main,
  Cancelled: colors.error.main,
};

const monthDefault = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => {
  if (!m) return '';
  const [y, mn] = m.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(mn, 10) - 1]} ${y}`;
};

const PayrollRun = () => {
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  const attendanceOn = !!(isModuleEnabled && isModuleEnabled('salary_attendance'));
  const [pullingAttendance, setPullingAttendance] = useState(false);
  const [attView, setAttView] = useState(null); // { staffName, summaryRow, dates }
  const [attViewLoading, setAttViewLoading] = useState(false);
  const { canEdit, canDelete } = usePagePermissions('salary', 'payrollRun');
  const { formatCurrency } = useCurrency();
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    payrollMonth: monthDefault(),
    totalWorkingDays: 26,
    otHoursPerDay: 8,   // used to turn monthly salary into a per-hour OT rate
    otMultiplier: 2,    // OT premium for this run (editable; PK-law default 2×)
    otMethod: 'salary', // 'salary' = Basic÷days÷hrs×mult; 'fixed' = staff OT Rate/Hour × hours
    notes: '',
    status: 'Draft'
  });
  const [lines, setLines] = useState([]);

  const [bankAccounts, setBankAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]); // Pay All by cash (2026-10-09)
  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => {
    if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig);
  }, [activeCompany]);

  const handlePrintPayslip = (line) => {
    printDocument('payslip', {
      ...(editing || {}),
      docNumber: editing?.docNumber || formData.docDate,
      payrollMonth: formData.payrollMonth,
      line
    }, activeCompany, printConfig, activeBranch);
  };
  const [showPayModal, setShowPayModal] = useState(false);
  useEscapeClose(showPayModal, () => setShowPayModal(false));
  const [payAccountId, setPayAccountId] = useState('');
  const [payMethod, setPayMethod] = useState('bank'); // 'bank' | 'cash'

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  useEscapeClose(showDetailsModal, () => setShowDetailsModal(false));
  const [editingLineIndex, setEditingLineIndex] = useState(-1);

  // ─── Load data ───
  const loadRuns = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const txs = await getTransactions(activeCompany.id, {
        tranTypeCode: TRAN_TYPE_CODE,
        ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {})
      });
      setRuns(txs);
    } catch (e) { console.error('Load PAY error:', e); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch]);

  const loadBankAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    const snap = await getDocs(collection(db, 'companies', activeCompany.id, 'accounts'));
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const pick = (prefix) => all
      .filter(a => a.level === 5 && a.code?.startsWith(prefix) && a.isActive !== false)
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    setBankAccounts(pick('11102'));
    setCashAccounts(pick('11101')); // Cash in Hand / Petty Cash …
  }, [activeCompany]);

  useEffect(() => { loadRuns(); loadBankAccounts(); }, [loadRuns, loadBankAccounts]);

  // ─── Form helpers ───
  const resetForm = () => {
    setEditing(null);
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      payrollMonth: monthDefault(),
      totalWorkingDays: 26,
      otHoursPerDay: 8,
      otMultiplier: 2,
      otMethod: 'salary',
      notes: '',
      status: 'Draft'
    });
    setLines([]);
  };

  const openNew = () => {
    if (!activeFinancialYear?.id) { alert('Activate a financial year first.'); return; }
    if (activeBranch && activeBranch.id === 'all') {
      alert('Pick a specific branch before creating a Payroll Run.');
      return;
    }
    resetForm();
    setViewMode('form');
  };

  const openEdit = (run) => {
    setEditing(run);
    setFormData({
      docDate: run.docDate || '',
      payrollMonth: run.payrollMonth || monthDefault(),
      totalWorkingDays: run.totalWorkingDays || 26,
      otHoursPerDay: run.otHoursPerDay ?? 8,
      otMultiplier: run.otMultiplier ?? 2,
      otMethod: run.otMethod || 'salary',
      notes: run.notes || '',
      status: run.status || 'Draft'
    });
    setLines(run.payrollLines || []);
    setViewMode('form');
  };

  const goBack = () => { setViewMode('list'); resetForm(); };

  useOpenFromReport(runs, openEdit);

  // ─── Generate Payroll: pre-fill all active staff ───
  const handleGenerate = async () => {
    if (lines.length > 0 && !window.confirm('This will replace all current lines. Continue?')) return;
    if (!formData.payrollMonth) { alert('Pick a month first.'); return; }

    setGenerating(true);
    try {
      // Block duplicate run for same month + branch
      const dup = runs.find(r =>
        r.id !== editing?.id &&
        r.payrollMonth === formData.payrollMonth &&
        (r.branchId || null) === (activeBranch?.id || null) &&
        r.status !== 'Cancelled'
      );
      if (dup) {
        if (!window.confirm(`A payroll run for ${monthLabel(formData.payrollMonth)} already exists (${dup.docNumber}, ${dup.status}). Generate anyway?`)) {
          setGenerating(false);
          return;
        }
      }

      // Load active staff (filter client-side to avoid needing composite index)
      const staffSnap = await getDocs(query(
        collection(db, 'companies', activeCompany.id, 'salaryStaff'),
        orderBy('staffCode')
      ));
      const staff = staffSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => s.isActive !== false);
      if (staff.length === 0) {
        alert('No active Salary Staff found. Add staff first under Salary → Salary Staff.');
        setGenerating(false);
        return;
      }

      // Load recoveries (advance + loan)
      const recoveriesMap = await loadRecoveriesForStaff(
        activeCompany.id,
        staff.map(s => s.id),
        formData.payrollMonth
      );

      // Build lines
      const newLines = staff.map(s => buildPayrollLine(
        s,
        {
          totalWorkingDays: Number(formData.totalWorkingDays) || 26,
          present: Number(formData.totalWorkingDays) || 26,  // default full attendance
          absent: 0,
          paidLeave: 0
        },
        recoveriesMap[s.id] || { advanceTotal: 0, advanceRefs: [], loanTotal: 0, loanRefs: [] }
      ));
      setLines(newLines);
    } catch (e) {
      console.error('Generate payroll error:', e);
      alert('Failed to generate: ' + (e?.message || ''));
    } finally { setGenerating(false); }
  };

  // ─── Per-line attendance edits ───
  const updateLineAttendance = (idx, patch) => {
    setLines(prev => prev.map((l, i) => i === idx ? recomputeLine({ ...l, ...patch }) : l));
  };

  // ─── Attendance add-on: fill attendance from machine logs (only when module enabled) ───
  const handlePullAttendance = async () => {
    if (!attendanceOn || lines.length === 0) return;
    if (!formData.payrollMonth) { alert('Pick a month first.'); return; }
    setPullingAttendance(true);
    try {
      const [settings, logs, adjustments, staffSnap] = await Promise.all([
        loadAttendanceSettings(activeCompany.id),
        fetchMonthLogs(activeCompany.id, formData.payrollMonth),
        fetchMonthAdjustments(activeCompany.id, formData.payrollMonth),
        getDocs(collection(db, 'companies', activeCompany.id, 'salaryStaff')),
      ]);
      const staff = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const summary = buildMonthlySummary({ month: formData.payrollMonth, logs, staff, settings, adjustments });
      let filled = 0;
      const unmapped = [];
      const next = lines.map(l => {
        const s = staff.find(x => x.id === l.staffId);
        const r = summary.byStaff[l.staffId];
        if (!s || !r) return l;
        if (!s.deviceUserId) { unmapped.push(l.staffName); return l; }
        filled++;
        const otHours = Number(r.payroll.otHours) || 0;
        const otAmt = otHours > 0
          ? ((formData.otMethod || 'salary') === 'fixed'
              ? computeOtFixed(otHours, l.otRatePerHour)
              : computeOtAmount(l.basicSalary, r.payroll.totalWorkingDays, formData.otHoursPerDay, otHours, formData.otMultiplier))
          : (Number(l.overtime) || 0);
        return recomputeLine({
          ...l,
          totalWorkingDays: r.payroll.totalWorkingDays,
          present: r.payroll.present,
          absent: r.payroll.absent,
          paidLeave: r.payroll.paidLeave,
          otHours: otHours > 0 ? otHours : (l.otHours || 0),
          overtime: otAmt,
        });
      });
      setLines(next);
      if (filled > 0) setFormData(f => ({ ...f, totalWorkingDays: summary.totalWorkingDays }));
      let msg = `Attendance pulled for ${filled} staff from machine logs (${logs.length} punches in ${formData.payrollMonth}).`;
      if (unmapped.length) msg += `\n\nNot mapped to a machine ID (left unchanged): ${unmapped.join(', ')}.\nMap them under Salary → Attendance → Machine ID Mapping.`;
      alert(msg);
    } catch (e) {
      console.error('Pull attendance error:', e);
      alert('Failed to pull attendance: ' + (e?.message || ''));
    } finally { setPullingAttendance(false); }
  };

  const removeLine = (idx) => {
    if (!window.confirm('Skip this staff from this payroll run?')) return;
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Per-staff attendance viewer (module-gated) ───
  const openAttendanceView = async (line) => {
    if (!attendanceOn) return;
    setAttViewLoading(true);
    setAttView({ staffName: line.staffName, loading: true });
    try {
      const [settings, logs, adjustments, staffSnap] = await Promise.all([
        loadAttendanceSettings(activeCompany.id),
        fetchMonthLogs(activeCompany.id, formData.payrollMonth),
        fetchMonthAdjustments(activeCompany.id, formData.payrollMonth),
        getDocs(collection(db, 'companies', activeCompany.id, 'salaryStaff')),
      ]);
      const staff = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const summary = buildMonthlySummary({ month: formData.payrollMonth, logs, staff, settings, adjustments });
      const row = summary.byStaff[line.staffId];
      const mapped = !!staff.find(s => s.id === line.staffId)?.deviceUserId;
      setAttView({ staffName: line.staffName, row, dates: summary.dates, settings, mapped, month: formData.payrollMonth });
    } catch (e) {
      alert('Failed to load attendance: ' + (e?.message || ''));
      setAttView(null);
    } finally { setAttViewLoading(false); }
  };

  // ─── Per-line details modal ───
  const openDetails = (idx) => {
    setEditingLineIndex(idx);
    setShowDetailsModal(true);
  };

  const updateLineFields = (patch) => {
    setLines(prev => prev.map((l, i) => i === editingLineIndex ? recomputeLine({ ...l, ...patch }) : l));
  };

  const updateDeductionInLine = (deductionIdx, patch) => {
    setLines(prev => prev.map((l, i) => {
      if (i !== editingLineIndex) return l;
      const newDeductions = (l.deductions || []).map((d, di) => di === deductionIdx ? { ...d, ...patch } : d);
      return recomputeLine({ ...l, deductions: newDeductions });
    }));
  };

  // Re-derive the auto-overtime amount for every staff line when the run-level
  // OT settings (hours/day or multiplier) change. Lines with 0 OT hours keep
  // their existing (possibly manually typed) overtime amount untouched.
  const recomputeAllOt = (hpd, mult, method) => {
    const m = method || formData.otMethod || 'salary';
    setLines(prev => prev.map(l => {
      const otHours = Number(l.otHours) || 0;
      if (otHours <= 0) return l;
      const amt = m === 'fixed'
        ? computeOtFixed(otHours, l.otRatePerHour)
        : computeOtAmount(l.basicSalary, l.totalWorkingDays, hpd, otHours, mult);
      return recomputeLine({ ...l, overtime: amt });
    }));
  };

  const detailLine = editingLineIndex >= 0 ? lines[editingLineIndex] : null;

  // ─── Aggregates ───
  const totals = useMemo(() => {
    let gross = 0, statutory = 0, loan = 0, advance = 0, net = 0;
    lines.forEach(l => {
      gross += Number(l.adjustedGross) || 0;
      statutory += Number(l.statutoryTotal) || 0;
      loan += Number(l.loanInstallment) || 0;
      advance += Number(l.advanceRecovery) || 0;
      net += Number(l.netPayable) || 0;
    });
    return { gross, statutory, loan, advance, net };
  }, [lines]);

  // ─── Validate + Save ───
  const validate = () => {
    if (lines.length === 0) return 'Generate the payroll first (no staff lines).';
    if (Number(formData.totalWorkingDays) <= 0) return 'Total Working Days must be greater than zero.';
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (Number(l.netPayable) < 0) return `${l.staffName}: net payable cannot be negative. Reduce loan/advance recovery.`;
      const checkSum = (Number(l.present) || 0) + (Number(l.absent) || 0) + (Number(l.paidLeave) || 0);
      if (checkSum > (Number(l.totalWorkingDays) || 0) + 0.01) {
        // Soft warning only via confirm — handled separately
      }
    }
    return null;
  };

  const buildTransactionData = (status) => ({
    tranTypeCode: TRAN_TYPE_CODE,
    docDate: formData.docDate,
    financialYearId: activeFinancialYear?.id,
    branchId: activeBranch?.id || null,
    branchName: activeBranch?.branchName || null,
    payrollMonth: formData.payrollMonth,
    totalWorkingDays: Number(formData.totalWorkingDays) || 26,
    otHoursPerDay: Number(formData.otHoursPerDay) || 8,
    otMultiplier: Number(formData.otMultiplier) || 0,
    otMethod: formData.otMethod || 'salary',
    payrollLines: lines,
    notes: formData.notes,
    // Aggregates (denormalized for fast list/report rendering)
    totalGrossSum: totals.gross,
    totalStatutorySum: totals.statutory,
    totalLoanRecoverySum: totals.loan,
    totalAdvanceRecoverySum: totals.advance,
    totalNetPayableSum: totals.net,
    staffCount: lines.length,
    // Standard transaction-level totals
    subtotal: totals.gross,
    discountAmount: 0,
    taxableAmount: totals.gross,
    taxAmount: 0,
    whtRate: 0, whtAmount: 0,
    totalAmount: totals.net,           // bank-payable amount
    paidAmount: editing?.paidAmount || 0,
    balanceAmount: totals.net - (editing?.paidAmount || 0),
    bulkPaymentTransactionId: editing?.bulkPaymentTransactionId || null,
    bulkPaymentDocNumber: editing?.bulkPaymentDocNumber || null,
    status
  });

  // Loan Recovery: part of a loan / advance may have been paid back in cash or
  // bank after this payroll was prepared. Before posting, warn when a staff's
  // deduction is more than what is still left. Reads 1 doc per loan / advance.
  const confirmDeductionsLeft = async (payrollLines) => {
    try {
      const over = await deductionsAboveBalance(activeCompany.id, payrollLines);
      if (over.length === 0) return true;
      const list = over.slice(0, 5).map(o =>
        `  • ${o.staffName}: ${o.kind} deduction ${formatCurrency(o.deduction)} — only ${formatCurrency(o.left)} left`).join('\n');
      const more = over.length > 5 ? `\n  …and ${over.length - 5} more` : '';
      return window.confirm(`Some loan / advance deductions are more than what the staff still owes (part was paid back after this payroll was prepared):\n\n${list}${more}\n\nPress Cancel and Re-generate the payroll (or correct those deductions). Press OK to post anyway.`);
    } catch (e) {
      console.warn('Loan / advance balance check skipped:', e);
      return true;
    }
  };

  const handleSave = async (status = 'Draft') => {
    if (!canEdit) { alert('No permission.'); return; }
    const err = validate();
    if (err) { alert(err); return; }

    // Soft attendance mismatch warning — Present + Absent + Paid Leave should equal Working Days
    const mismatched = lines.filter(l => {
      const sum = (Number(l.present) || 0) + (Number(l.absent) || 0) + (Number(l.paidLeave) || 0);
      return Math.abs(sum - (Number(l.totalWorkingDays) || 0)) > 0.01;
    });
    if (mismatched.length > 0) {
      const examples = mismatched.slice(0, 3).map(l => {
        const sum = (Number(l.present) || 0) + (Number(l.absent) || 0) + (Number(l.paidLeave) || 0);
        return `  • ${l.staffName}: Present ${l.present} + Absent ${l.absent} + Paid Leave ${l.paidLeave} = ${sum} (expected ${l.totalWorkingDays})`;
      }).join('\n');
      const more = mismatched.length > 3 ? `\n  …and ${mismatched.length - 3} more` : '';
      const msg = `Attendance check: Present + Absent + Paid Leave should equal Working Days for each staff.\n\n${mismatched.length} staff have a mismatch:\n${examples}${more}\n\nThis is usually a data-entry issue, but you can continue if it's intentional (e.g. mid-month joiner). Continue?`;
      if (!window.confirm(msg)) return;
    }

    if (status === 'Posted' && !(editing && editing.status === 'Posted')
      && !(await confirmDeductionsLeft(lines))) return;

    setSaving(true);
    try {
      const data = buildTransactionData(status);

      // If editing a Posted run and re-saving, reverse old recoveries first
      if (editing && editing.status === 'Posted' && (editing.payrollLines || []).length > 0) {
        await reverseRecoveries(activeCompany.id, editing.payrollLines);
      }

      let saved;
      let newTranData = null;
      if (editing) {
        saved = await updateTransaction(activeCompany.id, editing.id, data, currentUser.uid);
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
        saved = newTranData;
      }

      // Apply recoveries on Posted (or re-Posted)
      if (status === 'Posted') {
        await applyRecoveries(activeCompany.id, lines, saved.id);
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editing ? editing.id : newTranData?.id;
      const savedRecord = editing
        ? { ...editing, ...data, id: savedId, status }
        : { ...newTranData, status };
      setRuns(prev => editing
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      goBack();

      // Background refresh — reconciles with server data without blocking UI.
      loadRuns().catch(err => console.error('Background refresh failed:', err));
    } catch (e) {
      console.error('Save PAY error:', e);
      alert('Failed: ' + (e?.message || ''));
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (run, newStatus) => {
    if (!canEdit) { alert('No permission.'); return; }
    const cancellingPaid = newStatus === 'Cancelled' && run.status === 'Paid' && run.bulkPaymentTransactionId;
    const msg = cancellingPaid
      ? `Cancel ${run.docNumber}?\n\nThis also REVERSES the ${run.bulkPaymentType === 'CP' ? 'cash' : 'bank'} payment of ${formatCurrency(run.paidAmount || 0)}${run.bulkPaymentDocNumber ? ` (${run.bulkPaymentDocNumber})` : ''} and all recoveries.`
      : `Change ${run.docNumber} status to "${newStatus}"?`;
    if (!window.confirm(msg)) return;
    if (newStatus === 'Posted' && run.status !== 'Posted'
      && !(await confirmDeductionsLeft(run.payrollLines || []))) return;
    try {
      // Flip status FIRST, then reverse — so a failed status change can never leave
      // a loan/advance half-reversed (that's what got them stuck before).
      await updateTransactionStatus(activeCompany.id, run.id, newStatus, currentUser.uid);
      if (newStatus === 'Cancelled' && (run.status === 'Posted' || run.status === 'Paid')) {
        // Reverse the bulk salary Bank Payment (so the disbursement isn't orphaned),
        // then the advance/loan recoveries. Best-effort on the BP if already gone.
        if (run.bulkPaymentTransactionId) {
          try {
            await deleteTransaction(activeCompany.id, run.bulkPaymentTransactionId);
            await updateDoc(doc(db, 'companies', activeCompany.id, 'transactions', run.id), {
              paidAmount: 0, bulkPaymentTransactionId: null, bulkPaymentDocNumber: null,
            });
          } catch (err) { console.warn('Bulk BP reverse skipped (may already be deleted):', err); }
        }
        await reverseRecoveries(activeCompany.id, run.payrollLines || []);
      }
      await loadRuns();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  const handleDelete = async (run) => {
    if (!canDelete) { alert('No permission.'); return; }
    const hasBP = !!run.bulkPaymentTransactionId;
    const msg = hasBP
      ? `Delete ${run.docNumber}?\n\nThis also REVERSES the ${run.bulkPaymentType === 'CP' ? 'cash' : 'bank'} payment of ${formatCurrency(run.paidAmount || 0)}${run.bulkPaymentDocNumber ? ` (${run.bulkPaymentDocNumber})` : ''}, all recoveries, and the journal.`
      : `Delete ${run.docNumber}? Reverses all recoveries and removes the journal.`;
    if (!window.confirm(msg)) return;
    try {
      // Undo the bulk salary Bank Payment first (reverses the bank + staff legs) so
      // the disbursement isn't orphaned. Best-effort: if it's already gone, keep
      // going — the delete-guard now skips PAY, so the payroll still deletes.
      if (run.bulkPaymentTransactionId) {
        try { await deleteTransaction(activeCompany.id, run.bulkPaymentTransactionId); }
        catch (err) { console.warn('Bulk BP reverse skipped (may already be deleted):', err); }
      }
      // Delete the payroll FIRST, then reverse recoveries — so a blocked/failed
      // delete can never leave a loan/advance half-reversed (the earlier bug).
      await deleteTransaction(activeCompany.id, run.id);
      if (run.status === 'Posted' || run.status === 'Paid') {
        await reverseRecoveries(activeCompany.id, run.payrollLines || []);
      }
      await loadRuns();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  // ─── Pay All: bulk Bank Payment for total net ───
  const openPayModal = (run) => {
    if (!canEdit) { alert('No permission.'); return; }
    if (run.status !== 'Posted') { alert('Only Posted payrolls can be paid.'); return; }
    setEditing(run);
    setPayMethod('bank');
    setPayAccountId(bankAccounts[0]?.id || '');
    setShowPayModal(true);
  };

  const choosePayMethod = (m) => {
    setPayMethod(m);
    setPayAccountId(((m === 'cash' ? cashAccounts : bankAccounts)[0] || {}).id || '');
  };

  // Pay by Bank Payment (as before) or Cash Payment — same entries either way:
  // Dr each staff (net), Cr the chosen bank / cash account (total).
  const confirmPayAll = async () => {
    const isCash = payMethod === 'cash';
    if (!payAccountId) { alert(isCash ? 'Select a cash account.' : 'Select a bank account.'); return; }
    const bankAcc = (isCash ? cashAccounts : bankAccounts).find(b => b.id === payAccountId);
    if (!bankAcc) { alert(isCash ? 'Cash account not found.' : 'Bank account not found.'); return; }
    const run = editing;
    const unpaidLines = (run.payrollLines || []).filter(l => !l.paid && (Number(l.netPayable) || 0) > 0);
    const totalNet = unpaidLines.reduce((s, l) => s + (Number(l.netPayable) || 0), 0);
    if (totalNet <= 0) { alert('Nothing to pay (all net amounts are zero or already paid).'); return; }

    try {
      // Build manual sub-transaction entries: Dr each staff (net), Cr Bank (total)
      const subTransactionLines = [];
      unpaidLines.forEach(l => {
        if (l.staffAccountId && (Number(l.netPayable) || 0) > 0) {
          subTransactionLines.push({
            accountId: l.staffAccountId,
            accountCode: l.staffAccountCode || null,
            accountName: l.staffName ? `Staff: ${l.staffName}` : 'Staff Account',
            debitAmount: Math.round((Number(l.netPayable) || 0) * 100) / 100,
            creditAmount: 0,
            description: `${run.payrollMonth || ''} salary paid to ${l.staffName || ''}`.trim()
          });
        }
      });
      subTransactionLines.push({
        accountId: bankAcc.id,
        accountCode: bankAcc.code,
        accountName: bankAcc.name,
        debitAmount: 0,
        creditAmount: Math.round(totalNet * 100) / 100,
        description: `Payroll bulk disbursement ${run.docNumber}`
      });

      // Create a single bulk Bank Payment (or Cash Payment)
      const bp = await createTransaction(activeCompany.id, {
        tranTypeCode: isCash ? 'CP' : 'BP',
        docDate: new Date().toISOString().split('T')[0],
        financialYearId: run.financialYearId,
        branchId: run.branchId, branchName: run.branchName,
        partyType: null, partyId: null, partyName: 'Payroll Disbursement',
        ...(isCash
          ? { cashAccountId: bankAcc.id, cashAccountCode: bankAcc.code, cashAccountName: bankAcc.name }
          : { bankAccountId: bankAcc.id, bankAccountCode: bankAcc.code, bankAccountName: bankAcc.name }),
        subtotal: totalNet,
        totalAmount: totalNet,
        balanceAmount: 0,
        notes: `Bulk salary disbursement for ${run.docNumber} (${monthLabel(run.payrollMonth)})`,
        status: 'Posted',
        // Custom: link back to payroll
        payrollRunId: run.id,
        payrollRunDocNumber: run.docNumber,
        // Manual entries (bypass auto-gen)
        subTransactionLines
      }, currentUser.uid);

      // Mark payroll lines as paid + flip payroll status
      const updatedLines = (run.payrollLines || []).map(l =>
        unpaidLines.find(u => u.staffId === l.staffId)
          ? { ...l, paid: true, paidAt: new Date().toISOString() }
          : l
      );
      await updateDoc(doc(db, 'companies', activeCompany.id, 'transactions', run.id), {
        payrollLines: updatedLines,
        paidAmount: (run.paidAmount || 0) + totalNet,
        balanceAmount: 0,
        bulkPaymentTransactionId: bp.id,
        bulkPaymentDocNumber: bp.docNumber,
        bulkPaymentType: isCash ? 'CP' : 'BP',
        status: 'Paid',
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      });

      setShowPayModal(false);
      await loadRuns();
      alert(`Paid ${unpaidLines.length} staff via ${isCash ? 'Cash' : 'Bank'} Payment ${bp.docNumber}. Total: ${formatCurrency(totalNet)}`);
    } catch (e) {
      console.error('Pay All error:', e);
      alert('Failed: ' + (e?.message || ''));
    }
  };

  useDocumentShortcuts({
    onNew: openNew,
    onSave: () => handleSave(formData.status === 'Posted' ? 'Posted' : 'Draft'),
    viewMode,
    saving
  });

  const filtered = runs.filter(r => {
    const m = !searchQuery ||
      r.docNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.payrollMonth?.toLowerCase().includes(searchQuery.toLowerCase());
    const s = statusFilter === 'All' || r.status === statusFilter;
    return m && s;
  });

  // ═══════════════════════════════════════════════════════════ LIST
  if (viewMode === 'list') {
    return (
      <PageLayout pageTitle="Payroll Run" breadcrumb="Salary / Payroll Run">
        <ActionBar>
          <SearchBox>
            <FiSearch />
            <input type="text" placeholder="Search doc# or month (YYYY-MM)..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchBox>
          <FilterGroup>
            <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </StatusFilterSelect>
          </FilterGroup>
          {canEdit && <TranAddButton onClick={openNew}><FiPlus /> New Payroll Run</TranAddButton>}
        </ActionBar>

        {loading ? <LoadingMessage>Loading...</LoadingMessage>
          : filtered.length === 0 ? (
            <TranEmptyState>
              {searchQuery || statusFilter !== 'All'
                ? 'No payrolls match your filters.'
                : 'No payroll runs yet. Click "New Payroll Run" to start the first one.'}
            </TranEmptyState>
          ) : (
            <TableContainer>
              <Table>
                <thead><tr>
                  <Th>Doc #</Th><Th>Month</Th><Th>Date</Th><Th>Staff</Th>
                  <Th style={{ textAlign: 'right' }}>Gross</Th>
                  <Th style={{ textAlign: 'right' }}>Statutory</Th>
                  <Th style={{ textAlign: 'right' }}>Loan</Th>
                  <Th style={{ textAlign: 'right' }}>Advance</Th>
                  <Th style={{ textAlign: 'right' }}>Net Paid</Th>
                  {showBranchCol && <Th>Branch</Th>}
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr></thead>
                <tbody>
                  {filtered.map(r => (
                    <Tr key={r.id}>
                      <Td><strong>{r.docNumber}</strong></Td>
                      <Td><MonthBadge>{monthLabel(r.payrollMonth)}</MonthBadge></Td>
                      <Td>{r.docDate}</Td>
                      <Td style={{ textAlign: 'center' }}>{r.staffCount || (r.payrollLines?.length || 0)}</Td>
                      <Td style={{ textAlign: 'right' }}>{formatCurrency(r.totalGrossSum || 0)}</Td>
                      <Td style={{ textAlign: 'right' }}>{formatCurrency(r.totalStatutorySum || 0)}</Td>
                      <Td style={{ textAlign: 'right' }}>{formatCurrency(r.totalLoanRecoverySum || 0)}</Td>
                      <Td style={{ textAlign: 'right' }}>{formatCurrency(r.totalAdvanceRecoverySum || 0)}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(r.totalNetPayableSum || 0)}</Td>
                      {showBranchCol && <Td>{r.branchName || '-'}</Td>}
                      <Td>
                        <StatusBadge style={{ background: STATUS_COLORS[r.status] + '20', color: STATUS_COLORS[r.status] }}>
                          {r.status}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <ActionGroup>
                          <IconBtn title="View / Edit" onClick={() => openEdit(r)}><FiEye /></IconBtn>
                          {canEdit && r.status === 'Draft' && (
                            <IconBtn title="Post Payroll" onClick={() => handleStatusChange(r, 'Posted')}><FiCheck /></IconBtn>
                          )}
                          {canEdit && r.status === 'Posted' && (
                            <IconBtn title="Pay All (Bank or Cash Payment)" onClick={() => openPayModal(r)}>
                              <FiDollarSign />
                            </IconBtn>
                          )}
                          {canEdit && (r.status === 'Posted' || r.status === 'Paid') && (
                            <IconBtn title="Cancel (reverses recoveries)" onClick={() => handleStatusChange(r, 'Cancelled')}>
                              <FiXCircle />
                            </IconBtn>
                          )}
                          {canDelete && (
                            <IconBtn $danger title="Delete" onClick={() => handleDelete(r)}><FiTrash2 /></IconBtn>
                          )}
                        </ActionGroup>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          )}

        {/* Pay All modal */}
        {showPayModal && (
          <Overlay onClick={() => setShowPayModal(false)}>
            <PayModal onClick={e => e.stopPropagation()}>
              <ModalH>
                <h3>Pay All Salaries — {editing?.docNumber}</h3>
                <CloseBtn onClick={() => setShowPayModal(false)}><FiX /></CloseBtn>
              </ModalH>
              <ModalB>
                <PayMethodPicker value={payMethod} onChange={choosePayMethod} />
                <p style={{ marginBottom: spacing.md }}>
                  This will create <strong>one bulk {payMethod === 'cash' ? 'Cash' : 'Bank'} Payment</strong> debiting each staff's account
                  (their net) and crediting the selected {payMethod === 'cash' ? 'cash' : 'bank'} account for the total.
                </p>
                <FormGroup>
                  <Label>{payMethod === 'cash' ? 'Pay From Cash Account *' : 'Pay From Bank Account *'}</Label>
                  <Select value={payAccountId} onChange={e => setPayAccountId(e.target.value)}>
                    <option value="">— Select —</option>
                    {(payMethod === 'cash' ? cashAccounts : bankAccounts).map(b => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                  </Select>
                </FormGroup>
                <SummaryBox>
                  <span>Total Net to Pay:</span>
                  <strong>{formatCurrency((editing?.payrollLines || []).filter(l => !l.paid).reduce((s, l) => s + (Number(l.netPayable) || 0), 0))}</strong>
                </SummaryBox>
              </ModalB>
              <ModalF>
                <CancelBtn onClick={() => setShowPayModal(false)}>Cancel</CancelBtn>
                <ConfirmBtn onClick={confirmPayAll}>Confirm & Disburse</ConfirmBtn>
              </ModalF>
            </PayModal>
          </Overlay>
        )}
      </PageLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════ FORM
  const isEditable = !editing || editing.status === 'Draft';

  return (
    <PageLayout pageTitle="Payroll Run" breadcrumb="Salary / Payroll Run">
      <FormHeader>
        <BackButton onClick={goBack}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{editing ? `Edit ${editing.docNumber} — ${monthLabel(editing.payrollMonth)}` : 'New Payroll Run'}</FormTitle>
        <FormActions>
          {isEditable && (
            <SaveButton type="button" onClick={() => handleSave('Draft')} disabled={saving || lines.length === 0}>
              Save Draft
            </SaveButton>
          )}
          {isEditable && (
            <SaveButton type="button" onClick={() => handleSave('Posted')} disabled={saving || lines.length === 0}
              style={{ background: colors.success.main }}>
              {saving ? 'Saving...' : 'Save & Post'}
            </SaveButton>
          )}
        </FormActions>
      </FormHeader>

      <FormCard>
        <SectionTitle>Run Setup</SectionTitle>
        <SetupGrid>
          <FormGroup>
            <Label>Doc Date *</Label>
            <Input type="date" value={formData.docDate}
              onChange={e => setFormData({...formData, docDate: e.target.value})}
              disabled={!isEditable} />
          </FormGroup>
          <FormGroup>
            <Label>Payroll Month *</Label>
            <Input type="month" value={formData.payrollMonth}
              onChange={e => setFormData({...formData, payrollMonth: e.target.value})}
              disabled={!isEditable || lines.length > 0} />
          </FormGroup>
          <FormGroup>
            <Label>Total Working Days (default for all staff)</Label>
            <Input type="number" min="1" max="31" step="1" value={formData.totalWorkingDays}
              onChange={e => setFormData({...formData, totalWorkingDays: e.target.value})}
              disabled={!isEditable} />
          </FormGroup>
          <FormGroup>
            <Label>OT Method</Label>
            <Select value={formData.otMethod || 'salary'}
              onChange={e => { const v = e.target.value; setFormData(f => ({ ...f, otMethod: v })); recomputeAllOt(Number(formData.otHoursPerDay) || 0, Number(formData.otMultiplier) || 0, v); }}
              disabled={!isEditable} title="By Salary: Basic ÷ days ÷ hours × multiplier. Fixed Per-Hour: each staff's own OT Rate/Hour × OT hours.">
              <option value="salary">By Salary (Basic ÷ days ÷ hours)</option>
              <option value="fixed">Fixed Per-Hour (staff OT rate)</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>OT Hours / Day</Label>
            <Input type="number" min="1" max="24" step="0.5" value={formData.otHoursPerDay}
              onChange={e => { const v = e.target.value; setFormData(f => ({ ...f, otHoursPerDay: v })); recomputeAllOt(Number(v) || 0, Number(formData.otMultiplier) || 0); }}
              disabled={!isEditable || formData.otMethod === 'fixed'} title="Standard hours in a working day — used to turn monthly salary into a per-hour OT rate (By Salary method only)" />
          </FormGroup>
          <FormGroup>
            <Label>OT Multiplier (×)</Label>
            <Input type="number" min="0" step="0.5" value={formData.otMultiplier}
              onChange={e => { const v = e.target.value; setFormData(f => ({ ...f, otMultiplier: v })); recomputeAllOt(Number(formData.otHoursPerDay) || 0, Number(v) || 0); }}
              disabled={!isEditable || formData.otMethod === 'fixed'} title="Overtime premium for this run — e.g. 2 = double the ordinary hourly rate (By Salary method only)" />
          </FormGroup>
          {isEditable && (
            <FormGroup>
              <Label>&nbsp;</Label>
              <GenerateBtn onClick={handleGenerate} disabled={generating}>
                <FiPlay /> {generating ? 'Generating...' : (lines.length === 0 ? 'Generate Payroll' : 'Re-generate')}
              </GenerateBtn>
            </FormGroup>
          )}
          {isEditable && attendanceOn && lines.length > 0 && (
            <FormGroup>
              <Label>&nbsp;</Label>
              <GenerateBtn onClick={handlePullAttendance} disabled={pullingAttendance}
                title="Fill Present / Absent / Paid Leave for every line from the attendance machine logs of the selected month">
                <FiClock /> {pullingAttendance ? 'Pulling...' : 'Pull from Attendance'}
              </GenerateBtn>
            </FormGroup>
          )}
        </SetupGrid>
      </FormCard>

      {lines.length === 0 ? (
        <EmptyMessage>
          <FiInfo size={32} style={{ color: colors.info.main, marginBottom: 8 }} />
          <p>Set the month + total working days above, then click <strong>Generate Payroll</strong> to pre-fill all active staff with their salary structure, outstanding advances, and active loan installments.</p>
        </EmptyMessage>
      ) : (
        <FormCard>
          <SectionTitle>Staff Payroll Lines ({lines.length})</SectionTitle>
          <AttendanceHelp>
            <strong>Attendance rule:</strong> for each staff, <code>Present + Absent + Paid Leave</code> should equal <code>Working Days</code> (every day accounted for).
            &nbsp;<strong>Payable Days = Present + Paid Leave</strong> — earnings prorate on this fraction of Working Days.
            Statutory deductions, loan installments, and advance recoveries are <strong>not</strong> prorated by default; edit per row in the Details modal if needed.
          </AttendanceHelp>
          <PayrollTable>
            <thead>
              <tr>
                <PTh style={{ width: 30 }}>#</PTh>
                <PTh style={{ minWidth: 180 }}>Staff</PTh>
                <PTh style={{ width: 90, textAlign: 'center' }} title="Total Working Days for the month">Working Days</PTh>
                <PTh style={{ width: 80, textAlign: 'center' }} title="Days the staff was present at work">Present</PTh>
                <PTh style={{ width: 80, textAlign: 'center' }} title="Days the staff was absent (unpaid)">Absent</PTh>
                <PTh style={{ width: 90, textAlign: 'center' }} title="Days approved as paid leave (counts as worked)">Paid Leave</PTh>
                <PTh style={{ width: 90, textAlign: 'center' }} title="Auto = Present + Paid Leave. Earnings prorate on this.">Payable Days</PTh>
                <PTh style={{ width: 120, textAlign: 'right' }} title="Basic + Allowances, prorated by Payable Days">Effective Gross</PTh>
                <PTh style={{ width: 110, textAlign: 'right' }} title="Bonus + Arrears + OT − Leave Cut">+ Adjustments</PTh>
                <PTh style={{ width: 120, textAlign: 'right' }} title="EOBI, Income Tax, PF, etc. from staff structure">− Statutory</PTh>
                <PTh style={{ width: 110, textAlign: 'right' }} title="Auto-pulled from active loans starting this month">− Loan</PTh>
                <PTh style={{ width: 110, textAlign: 'right' }} title="Auto-pulled full outstanding from issued advances">− Advance</PTh>
                <PTh style={{ width: 130, textAlign: 'right' }} title="Effective Gross + Adjustments − Statutory − Loan − Advance">Net Payable</PTh>
                <PTh style={{ width: 110, textAlign: 'center' }}>Actions</PTh>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const adj = (Number(l.bonus) || 0) + (Number(l.arrears) || 0) + (Number(l.overtime) || 0) - (Number(l.leaveDeduction) || 0);
                const negative = Number(l.netPayable) < 0;
                return (
                  <PTr key={l.staffId || i} $negative={negative}>
                    <PTd>{i + 1}</PTd>
                    <PTd>
                      <strong>{l.staffName}</strong>
                      <Sub>{l.staffCode}{l.designation ? ` · ${l.designation}` : ''}</Sub>
                    </PTd>
                    <PTd><Inp type="number" min="0" max="31" value={l.totalWorkingDays}
                      onChange={e => updateLineAttendance(i, { totalWorkingDays: e.target.value })}
                      disabled={!isEditable} /></PTd>
                    <PTd><Inp type="number" min="0" value={l.present}
                      onChange={e => updateLineAttendance(i, { present: e.target.value })}
                      disabled={!isEditable} /></PTd>
                    <PTd><Inp type="number" min="0" value={l.absent}
                      onChange={e => updateLineAttendance(i, { absent: e.target.value })}
                      disabled={!isEditable} /></PTd>
                    <PTd><Inp type="number" min="0" value={l.paidLeave}
                      onChange={e => updateLineAttendance(i, { paidLeave: e.target.value })}
                      disabled={!isEditable} /></PTd>
                    <PTd style={{ textAlign: 'center', fontWeight: 600 }}>{l.payableDays}</PTd>
                    <PTd style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatAmount(l.effectiveGross)}</PTd>
                    <PTd style={{ textAlign: 'right', fontFamily: 'monospace', color: adj > 0 ? colors.success.dark : adj < 0 ? colors.error.main : colors.text.tertiary }}>
                      {adj !== 0 ? (adj > 0 ? '+' : '') + formatAmount(adj) : '0'}
                    </PTd>
                    <PTd style={{ textAlign: 'right', fontFamily: 'monospace', color: colors.warning.dark }}>−{formatAmount(l.statutoryTotal)}</PTd>
                    <PTd style={{ textAlign: 'right', fontFamily: 'monospace', color: colors.warning.dark }}>−{formatAmount(l.loanInstallment)}</PTd>
                    <PTd style={{ textAlign: 'right', fontFamily: 'monospace', color: colors.warning.dark }}>−{formatAmount(l.advanceRecovery)}</PTd>
                    <PTd style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: negative ? colors.error.main : colors.success.dark }}>
                      {formatAmount(l.netPayable)}
                    </PTd>
                    <PTd style={{ textAlign: 'center' }}>
                      {attendanceOn && (
                        <IconBtn title="View this staff's attendance for the month" onClick={() => openAttendanceView(l)}>
                          <FiCalendar />
                        </IconBtn>
                      )}
                      <IconBtn title="Details / Edit allowances + deductions" onClick={() => openDetails(i)}>
                        <FiEdit2 />
                      </IconBtn>
                      {isEditable && (
                        <IconBtn $danger title="Skip this staff" onClick={() => removeLine(i)}>
                          <FiX />
                        </IconBtn>
                      )}
                    </PTd>
                  </PTr>
                );
              })}
            </tbody>
            <tfoot>
              <FootRow>
                <td colSpan="7" style={{ textAlign: 'right', padding: spacing.sm }}><strong>TOTALS:</strong></td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{formatAmount(lines.reduce((s, l) => s + (Number(l.effectiveGross) || 0), 0))}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{formatAmount(totals.gross - lines.reduce((s, l) => s + (Number(l.effectiveGross) || 0), 0))}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: colors.warning.dark }}>−{formatAmount(totals.statutory)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: colors.warning.dark }}>−{formatAmount(totals.loan)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: colors.warning.dark }}>−{formatAmount(totals.advance)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: colors.success.dark, fontSize: 14 }}>{formatAmount(totals.net)}</td>
                <td></td>
              </FootRow>
            </tfoot>
          </PayrollTable>
        </FormCard>
      )}

      {/* ─── Per-line Details Modal ─── */}
      {showDetailsModal && detailLine && (
        <Overlay onClick={() => setShowDetailsModal(false)}>
          <DetailModal onClick={e => e.stopPropagation()}>
            <ModalH>
              <h3>{detailLine.staffName} — {monthLabel(formData.payrollMonth)}</h3>
              <CloseBtn onClick={() => setShowDetailsModal(false)}><FiX /></CloseBtn>
            </ModalH>
            <ModalB>
              <DetailSection>
                <DetailTitle>Earnings</DetailTitle>
                <DetailRow><span>Basic Salary</span><strong>{formatCurrency(detailLine.basicSalary)}</strong><span>Prorated</span><strong>{formatCurrency(detailLine.basicProrated)}</strong></DetailRow>
                {(detailLine.allowances || []).map((a, ai) => (
                  <DetailRow key={ai}>
                    <span>{a.name} {a.amountType === 'percent' ? `(${a.value}% of basic)` : ''}</span>
                    <strong>{formatCurrency(a.fullValue)}</strong>
                    <span>Prorated</span>
                    <strong>{formatCurrency(a.prorated)}</strong>
                  </DetailRow>
                ))}
                <DetailTotal><span>Effective Gross</span><strong>{formatCurrency(detailLine.effectiveGross)}</strong></DetailTotal>
              </DetailSection>

              <DetailSection>
                <DetailTitle>Adjustments (one-time, manual)</DetailTitle>
                <AdjGrid>
                  <FormGroup><Label>Bonus</Label>
                    <Input type="number" min="0" step="0.01" value={detailLine.bonus || 0}
                      onChange={e => updateLineFields({ bonus: e.target.value })} disabled={!isEditable} /></FormGroup>
                  <FormGroup><Label>Arrears</Label>
                    <Input type="number" min="0" step="0.01" value={detailLine.arrears || 0}
                      onChange={e => updateLineFields({ arrears: e.target.value })} disabled={!isEditable} /></FormGroup>
                  <FormGroup><Label>OT Hours</Label>
                    <Input type="number" min="0" step="0.5" value={detailLine.otHours || 0}
                      onChange={e => {
                        const otHours = e.target.value;
                        const otAmt = (formData.otMethod || 'salary') === 'fixed'
                          ? computeOtFixed(otHours, detailLine.otRatePerHour)
                          : computeOtAmount(detailLine.basicSalary, detailLine.totalWorkingDays, formData.otHoursPerDay, otHours, formData.otMultiplier);
                        updateLineFields({ otHours, overtime: otAmt });
                      }} disabled={!isEditable} />
                    {Number(detailLine.otHours) > 0 && (
                      <small style={{ display: 'block', marginTop: 4, color: '#666' }}>= {fmtHM(Number(detailLine.otHours) * 60)}</small>
                    )}</FormGroup>
                  <FormGroup><Label>Overtime Amount</Label>
                    <Input type="number" min="0" step="0.01" value={detailLine.overtime || 0}
                      onChange={e => updateLineFields({ overtime: e.target.value })} disabled={!isEditable} /></FormGroup>
                  <FormGroup><Label>Leave Cut</Label>
                    <Input type="number" min="0" step="0.01" value={detailLine.leaveDeduction || 0}
                      onChange={e => updateLineFields({ leaveDeduction: e.target.value })} disabled={!isEditable} /></FormGroup>
                </AdjGrid>
                {(() => {
                  if ((formData.otMethod || 'salary') === 'fixed') {
                    const rate = Number(detailLine.otRatePerHour) || 0;
                    return (
                      <EmptyHint>
                        {rate > 0
                          ? <>Fixed OT rate: <strong>{formatCurrency(rate)}</strong>/OT-hour (from this staff's record) × OT Hours.</>
                          : <>No OT Rate/Hour is set for this staff — set it on the staff record, or type the amount manually.</>}
                        {' '}Type OT Hours to auto-fill; the amount stays editable.
                      </EmptyHint>
                    );
                  }
                  const days = Number(detailLine.totalWorkingDays) || 0;
                  const hpd = Number(formData.otHoursPerDay) || 0;
                  const mult = Number(formData.otMultiplier) || 0;
                  const perHour = (days > 0 && hpd > 0) ? (Number(detailLine.basicSalary) || 0) / days / hpd : 0;
                  return (
                    <EmptyHint>
                      OT rate: Basic {formatCurrency(detailLine.basicSalary)} ÷ {days || '—'} days ÷ {hpd || '—'} hrs
                      = {formatCurrency(perHour)}/hr × {mult}× = <strong>{formatCurrency(perHour * mult)}</strong>/OT-hour.
                      Type OT Hours to auto-fill the amount; the amount stays editable for a manual override.
                    </EmptyHint>
                  );
                })()}
                <DetailTotal><span>Adjusted Gross</span><strong>{formatCurrency(detailLine.adjustedGross)}</strong></DetailTotal>
              </DetailSection>

              <DetailSection>
                <DetailTitle>Statutory Deductions (editable)</DetailTitle>
                {(detailLine.deductions || []).length === 0 ? (
                  <EmptyHint>No statutory deductions on this staff. Add via Salary Staff master.</EmptyHint>
                ) : (
                  (detailLine.deductions || []).map((d, di) => (
                    <DedRow key={di}>
                      <span>{d.name} {d.amountType === 'percent' ? `(${d.value}% of basic)` : ''}</span>
                      <Input type="number" min="0" step="0.01" value={d.computed || 0}
                        onChange={e => updateDeductionInLine(di, { computed: Number(e.target.value) || 0 })}
                        disabled={!isEditable}
                        style={{ maxWidth: 130, textAlign: 'right' }} />
                      <small style={{ color: colors.text.tertiary }}>→ {d.accountName || d.accountCode || '—'}</small>
                    </DedRow>
                  ))
                )}
                <DetailTotal><span>Statutory Total</span><strong>{formatCurrency(detailLine.statutoryTotal)}</strong></DetailTotal>
              </DetailSection>

              <DetailSection>
                <DetailTitle>Recoveries (editable; auto-pulled from outstanding ADV/LON)</DetailTitle>
                <AdjGrid>
                  <FormGroup>
                    <Label>Loan Installment {detailLine.loanRefs?.length > 0 && <small>({detailLine.loanRefs.length} loan(s))</small>}</Label>
                    <Input type="number" min="0" step="0.01" value={detailLine.loanInstallment || 0}
                      onChange={e => updateLineFields({ loanInstallment: Number(e.target.value) || 0 })}
                      disabled={!isEditable} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Advance Recovery {detailLine.advanceRefs?.length > 0 && <small>({detailLine.advanceRefs.length} adv.)</small>}</Label>
                    <Input type="number" min="0" step="0.01" value={detailLine.advanceRecovery || 0}
                      onChange={e => updateLineFields({ advanceRecovery: Number(e.target.value) || 0 })}
                      disabled={!isEditable} />
                  </FormGroup>
                </AdjGrid>
                {(detailLine.loanRefs || []).length > 0 && (
                  <RefList>
                    <strong>Linked loans:</strong> {(detailLine.loanRefs).map(r => `${r.docNumber} (${formatCurrency(r.amount)})`).join(', ')}
                  </RefList>
                )}
                {(detailLine.advanceRefs || []).length > 0 && (
                  <RefList>
                    <strong>Linked advances:</strong> {(detailLine.advanceRefs).map(r => `${r.docNumber} (${formatCurrency(r.amount)})`).join(', ')}
                  </RefList>
                )}
              </DetailSection>

              <NetSection $negative={Number(detailLine.netPayable) < 0}>
                <span>NET PAYABLE</span>
                <strong>{formatCurrency(detailLine.netPayable)}</strong>
                {Number(detailLine.netPayable) < 0 && (
                  <small>⚠ Negative — reduce loan/advance recovery before saving.</small>
                )}
              </NetSection>
            </ModalB>
            <ModalF>
              {editing && (
                <CancelBtn onClick={() => handlePrintPayslip(detailLine)}>
                  <FiPlus style={{ marginRight: 4 }} /> Print Payslip
                </CancelBtn>
              )}
              <ConfirmBtn onClick={() => setShowDetailsModal(false)}>Done</ConfirmBtn>
            </ModalF>
          </DetailModal>
        </Overlay>
      )}
      {attView && (
        <Overlay onClick={() => setAttView(null)}>
          <AttModal onClick={e => e.stopPropagation()}>
            <ModalHd>
              <div><strong>{attView.staffName}</strong><Sub>Attendance · {attView.month}</Sub></div>
              <IconBtn onClick={() => setAttView(null)}><FiX /></IconBtn>
            </ModalHd>
            {attViewLoading || !attView.row ? (
              <div style={{ padding: 24, textAlign: 'center', color: colors.text.secondary }}>
                {attViewLoading ? 'Loading…' : (attView.mapped === false ? 'This staff is not mapped to a machine ID. Map it in Salary → Attendance → Machine ID Mapping.' : 'No attendance found.')}
              </div>
            ) : (
              <>
                <AttStats>
                  <span><b>{attView.row.counts.present}</b> Present</span>
                  <span><b>{attView.row.counts.late}</b> Late</span>
                  <span><b>{attView.row.counts.half}</b> Half</span>
                  <span><b>{attView.row.counts.absent}</b> Absent</span>
                  <span><b>{attView.row.counts.paidLeave}</b> Paid Leave</span>
                  <span><b>{fmtHM(minutesOf(attView.row.counts.otMinutes, attView.row.counts.otHours))}</b> OT</span>
                </AttStats>
                <AttGridWrap>
                  <AttGrid>
                    <tbody>
                      {attView.dates.map(d => {
                        const day = attView.row.days[d];
                        const m = STATUS_META[day.status] || STATUS_META['-'];
                        return (
                          <tr key={d}>
                            <td className="dt">{d.slice(-2)} {WEEKDAY_NAMES[weekdayOf(d)]}</td>
                            <td><span style={{ background: m.bg, color: m.color, padding: '1px 7px', borderRadius: 5, fontWeight: 700, fontSize: 11 }}>{day.status}</span></td>
                            <td>{fmt12(day.inTime)}</td>
                            <td>{fmt12(day.outTime)}</td>
                            <td>{day.workedHours != null ? fmtHM(minutesOf(day.workedMinutes, day.workedHours)) : ''}</td>
                            <td>{day.otHours ? fmtHM(minutesOf(day.otMinutes, day.otHours)) + ' OT' : ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </AttGrid>
                </AttGridWrap>
              </>
            )}
          </AttModal>
        </Overlay>
      )}
    </PageLayout>
  );
};

// ─── Local Styles ───
const SetupGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
`;
const GenerateBtn = styled.button`
  display: flex; align-items: center; gap: 6px; justify-content: center;
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main}; color: white; border: none;
  border-radius: ${borderRadius.sm}; cursor: pointer;
  font-size: ${typography.fontSize.sm}; font-weight: 600;
  &:hover:not(:disabled) { background: ${colors.primary.dark}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const EmptyMessage = styled.div`
  padding: ${spacing.xxl}; text-align: center;
  background: ${colors.background.default}; border-radius: ${borderRadius.lg};
  border: 1px dashed ${colors.border.light};
  color: ${colors.text.secondary};
  margin-top: ${spacing.md};
  p { max-width: 600px; margin: 0 auto; line-height: 1.6; }
`;

const AttendanceHelp = styled.div`
  margin-bottom: ${spacing.md};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.info.main}10;
  border-left: 3px solid ${colors.info.main};
  border-radius: ${borderRadius.sm};
  font-size: 12px;
  color: ${colors.text.secondary};
  line-height: 1.6;
  code {
    background: white; padding: 1px 6px; border-radius: 3px;
    font-family: monospace; color: ${colors.primary.main};
  }
`;

const PayrollTable = styled.table`
  width: 100%; border-collapse: collapse; min-width: 1380px;
`;
const PTh = styled.th`
  padding: 8px 6px; background: ${colors.neutral[50]}; color: ${colors.text.secondary};
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px;
  border-bottom: 2px solid ${colors.border.light}; text-align: left; white-space: nowrap;
`;
const PTr = styled.tr`
  background: ${props => props.$negative ? colors.error.main + '10' : 'transparent'};
  &:hover { background: ${props => props.$negative ? colors.error.main + '15' : colors.neutral[50]}; }
  border-bottom: 1px solid ${colors.neutral[100]};
`;
const PTd = styled.td`
  padding: 6px; font-size: ${typography.fontSize.sm}; vertical-align: middle;
`;
const Sub = styled.div`font-size: 10px; color: ${colors.text.tertiary}; margin-top: 2px;`;
const Inp = styled.input`
  width: 100%; padding: 4px 6px; text-align: center;
  border: 1px solid ${colors.border.light}; border-radius: 3px;
  font-size: ${typography.fontSize.sm};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
  &:disabled { background: ${colors.neutral[100]}; color: ${colors.text.tertiary}; }
`;
const FootRow = styled.tr`
  background: ${colors.neutral[100]};
  td { padding: 8px 6px; border-top: 2px solid ${colors.border.light}; font-size: ${typography.fontSize.sm}; }
`;

const MonthBadge = styled.span`
  display: inline-block; padding: 2px 10px;
  background: ${colors.primary.main}15; color: ${colors.primary.main};
  border-radius: ${borderRadius.xl}; font-size: 11px; font-weight: 700;
`;

// Modals — match Banks/Customers master modal style
const Overlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
`;
const PayModal = styled.div`
  background: white; border-radius: ${borderRadius.lg};
  width: 100%; max-width: 500px;
  max-height: 92vh; overflow-y: auto;
  box-shadow: ${shadows.xl};
`;
const DetailModal = styled.div`
  background: white; border-radius: ${borderRadius.lg};
  width: 100%; max-width: 900px; max-height: 92vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;
const ModalH = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 24px; border-bottom: 1px solid ${colors.border.light};
  h3 {
    font-size: 18px; font-weight: 600; color: ${colors.text.primary}; margin: 0;
    display: flex; align-items: center; gap: 10px;
  }
`;
const ModalB = styled.div`padding: 24px;`;
const ModalF = styled.div`
  padding: 20px 24px; border-top: 1px solid ${colors.border.light};
  display: flex; justify-content: flex-end; gap: 12px;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  background: transparent; border: none;
  color: ${colors.text.tertiary}; cursor: pointer;
  border-radius: ${borderRadius.sm};
  &:hover { background: ${colors.background.paper}; color: ${colors.text.primary}; }
`;
const CancelBtn = styled.button`
  padding: 10px 20px;
  background: white; color: ${colors.text.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px; font-weight: 500;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: ${colors.background.paper}; }
`;
const ConfirmBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: ${borderRadius.md};
  font-size: 14px; font-weight: 500;
  cursor: pointer; transition: all 0.2s;
  &:hover:not(:disabled) { background: ${colors.primary.dark}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SummaryBox = styled.div`
  padding: ${spacing.md}; background: ${colors.primary.main}10;
  border-radius: ${borderRadius.sm};
  display: flex; justify-content: space-between; align-items: center;
  margin-top: ${spacing.md};
  span { color: ${colors.text.secondary}; }
  strong { font-size: ${typography.fontSize.xl}; color: ${colors.primary.main}; font-family: monospace; }
`;

// Detail modal sub-components
const DetailSection = styled.div`
  margin-bottom: ${spacing.lg};
  padding-bottom: ${spacing.md};
  border-bottom: 1px dashed ${colors.border.light};
  &:last-child { border-bottom: none; }
`;
const DetailTitle = styled.h4`
  font-size: ${typography.fontSize.md}; color: ${colors.primary.main};
  margin-bottom: ${spacing.sm};
`;
const DetailRow = styled.div`
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: ${spacing.sm}; padding: 4px 0; align-items: center;
  font-size: ${typography.fontSize.sm};
  span { color: ${colors.text.secondary}; }
  strong { font-family: monospace; text-align: right; }
`;
const DetailTotal = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-top: ${spacing.sm}; padding: 8px 12px;
  background: ${colors.neutral[50]}; border-radius: ${borderRadius.sm};
  span { font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; }
  strong { font-family: monospace; font-size: ${typography.fontSize.lg}; }
`;
const AdjGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${spacing.md};
`;
const DedRow = styled.div`
  display: grid; grid-template-columns: 2fr 1fr 1.5fr;
  gap: ${spacing.sm}; align-items: center;
  padding: 4px 0; font-size: ${typography.fontSize.sm};
  span { color: ${colors.text.secondary}; }
  small { font-size: 11px; }
`;
const EmptyHint = styled.p`
  padding: ${spacing.md}; text-align: center;
  color: ${colors.text.tertiary}; font-size: 12px; font-style: italic;
`;
const RefList = styled.div`
  margin-top: ${spacing.sm}; padding: ${spacing.sm} ${spacing.md};
  background: ${colors.neutral[50]}; border-radius: ${borderRadius.sm};
  font-size: 11px; color: ${colors.text.secondary};
  strong { color: ${colors.text.primary}; margin-right: 4px; }
`;
const NetSection = styled.div`
  margin-top: ${spacing.md}; padding: ${spacing.md} ${spacing.lg};
  background: ${props => props.$negative ? colors.error.main + '15' : colors.success.main + '15'};
  border-left: 4px solid ${props => props.$negative ? colors.error.main : colors.success.main};
  border-radius: ${borderRadius.sm};
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap;
  gap: ${spacing.md};
  span:first-child { font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; font-size: 12px; }
  strong { font-family: monospace; font-size: 24px; color: ${props => props.$negative ? colors.error.dark : colors.success.dark}; }
  small { width: 100%; color: ${colors.error.dark}; font-size: 11px; }
`;

const AttModal = styled.div`background:#fff;border-radius:12px;padding:18px 20px;width:min(560px,94vw);max-height:86vh;display:flex;flex-direction:column;`;
const ModalHd = styled.div`display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;`;
const AttStats = styled.div`display:flex;flex-wrap:wrap;gap:10px 18px;font-size:13px;margin-bottom:12px;span{color:#666}b{color:#222;font-size:15px;margin-right:2px}`;
const AttGridWrap = styled.div`overflow-y:auto;border:1px solid #eee;border-radius:8px;`;
const AttGrid = styled.table`width:100%;border-collapse:collapse;font-size:12px;td{padding:5px 8px;border-bottom:1px solid #f3f3f3}td.dt{white-space:nowrap;color:#555;width:90px}`;

export default PayrollRun;
